import React, { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import ReportSummaryCards from '../../components/report/ReportSummaryCards';
import SimpleTable from '../../components/report/SimpleTable';
import SimplePagination from '../../components/report/SimplePagination';
import { formatMoney, formatDateTime, statusBadgeClass } from '../../lib/reportHelpers';
import { send } from '../../components';
import { useLoading } from '../../hooks/useLoading';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
const defaultFilter = {
    invoice_id: '',
    // status: '',
    date_from: '',
    date_to: '',
};

const StatusBadge = ({ value }) => (
    <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${statusBadgeClass(value)}`}>
        {value || '-'}
    </span>
);

const InvoiceTransactionReport = () => {
    const [filters, setFilters] = useState(defaultFilter);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const { showLoading } = useLoading();
    const [pager, setPager] = useState({ skip: 0, take: 20 });
    const [total, setTotal] = useState(0);
    const { t } = useTranslation();

    const payload = useMemo(() => ({
        ...filters,
        statusid: filters.statusid === '' ? null : Number(filters.statusid),
        skip: pager.skip,
        take: pager.take,
        sort: [{ field: 'created_at', dir: 'desc' }],
    }), [filters, pager]);

    const getInvoiceTransactionReport = async (paramfilter) => {
        const res = await send("cu0909", paramfilter, showLoading);
        if (res.status === 'error') {
            toast.error(res.message);
            return;
        }
        setRows(res.data)
        setTotal(res.total)
    }

    const getInvoiceTransactionReportSummary = async (paramfilter) => {
        const res = await send("cu0910", paramfilter, showLoading);
        if (res.status === 'error') {
            toast.error(res.message);
            return;
        }
        setSummary(res)
    }

    const loadData = (customPayload = payload) => {
        getInvoiceTransactionReport(customPayload);

        const { skip, take, sort, ...summaryFilter } = customPayload;
        getInvoiceTransactionReportSummary(summaryFilter);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pager.skip, pager.take]);

    const onSearch = async () => {
        const next = { skip: 0, take: pager.take };
        setPager(next);
        await loadData({
            ...filters,
            statusid: filters.statusid === '' ? null : Number(filters.statusid),
            skip: 0,
            take: pager.take,
            sort: [{ field: 'created_at', dir: 'desc' }],
        });
    };

    const onReset = async () => {
        setFilters(defaultFilter);
        const nextPager = { skip: 0, take: 20 };
        setPager(nextPager);
        await loadData({
            ...defaultFilter,
            statusid: null,
            skip: 0,
            take: 20,
            sort: [{ field: 'created_at', dir: 'desc' }],
        });
    };
    const onExportExcel = async () => {
        const exportPayload = {
            ...filters,
            statusid: filters.statusid === '' ? null : Number(filters.statusid),
            skip: 0,
            take: total || 999999,
            sort: [{ field: 'created_at', dir: 'desc' }],
        };

        const res = await send("cu0909", exportPayload, showLoading);
        if (res.status === 'error') {
            toast.error(res.message);
            return;
        }

        const exportRows = (res.data || []).map((row, index) => ({
            '№': index + 1,
            [t('ref')]: row.invoice_id ?? '',
            [t('description')]: row.txndesc ?? '',
            [t('TranID')]: row.tranid ?? '',
            [t('amount')]: Number(row.amount || 0),
            [t('fee')]: Number(row.amount_fee || 0),
            [t('SettledAmount')]: Number(row.settle_amount || 0),
            [t('currency')]: row.currency ?? '',
            [t('status')]: row.status ?? '',
            [t('approvalcode')]: row.approvalcode ?? '',
            [t('paymentNote')]: row.description ?? '',
            [t('CreatedAt')]: formatDateTime(row.created_at),
            [t('SettleAt')]: formatDateTime(row.settle_at),
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportRows);

        worksheet['!cols'] = [
            { wch: 6 },
            { wch: 12 },
            { wch: 30 },
            { wch: 16 },
            { wch: 14 },
            { wch: 14 },
            { wch: 16 },
            { wch: 10 },
            { wch: 14 },
            { wch: 16 },
            { wch: 24 },
            { wch: 24 },
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'InvoiceTransactions');

        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
        });

        const blob = new Blob(
            [excelBuffer],
            {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
            }
        );

        const today = new Date();
        const fileName = `invoice_transaction_report_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}.xlsx`;

        saveAs(blob, fileName);
    };
    const summaryItems = [
        { title: t('totalTran'), value: Number(summary.approved_count || 0) },
        { title: t('totalAmountRpt'), value: Number(summary.total_amount || 0), money: true },
        { title: t('totalFee'), value: Number(summary.total_fee || 0), money: true },
        { title: t('totalSettled'), value: Number(summary.total_settle_amount || 0), money: true },
        // { title: 'Callback хийгдсэн', value: Number(summary.callbacked_count || 0) },
        // { title: 'Callback хийгдээгүй', value: Number(summary.not_callbacked_count || 0) },
        // { title: t('approved'), value: Number(summary.approved_count || 0) },
        // { title: t('pending'), value: Number(summary.pending_count || 0) },
        // { title: t('failed'), value: Number(summary.failed_count || 0) },
    ];

    const columns = [
        { key: 'invoice_id', title: t('ref'), width: 20 },
        { key: 'txndesc', title: t('description'), width: 300 },
        // { key: 'merchant_display_name', title: 'Merchant', width: 220 },
        // { key: 'customer_name', title: 'Customer', width: 180 },
        // { key: 'ordernum', title: 'Order No', width: 180 },
        // { key: 'checkid', title: 'Check ID', width: 180 },
        { key: 'tranid', title: t('TranID'), width: 100 },
        // { key: 'tranactionid', title: 'Transaction ID', width: 180 },
        // { key: 'terminalid', title: 'Terminal ID', width: 140 },
        { key: 'amount', title: t('amount'), align: 'right', render: (row) => formatMoney(row.amount) },
        { key: 'amount_fee', title: t('fee'), align: 'right', render: (row) => formatMoney(row.amount_fee) },
        { key: 'settle_amount', title: t('SettledAmount'), align: 'right', render: (row) => formatMoney(row.settle_amount) },
        { key: 'currency', title: t('currency') },
        { key: 'status', title: t('status'), render: (row) => <StatusBadge value={row.status} /> },
        // { key: 'statusid', title: 'Status ID' },
        { key: 'approvalcode', title: t('approvalcode') },
        { key: 'description', title: t('paymentNote'), width: 220 },
        // { key: 'maskedpan', title: 'Masked PAN' },
        // { key: 'callbacked_at', title: 'Callbacked At', render: (row) => formatDateTime(row.callbacked_at) },
        { key: 'created_at', title: t('CreatedAt'), render: (row) => formatDateTime(row.created_at) },
        { key: 'settle_at', title: t('SettleAt'), render: (row) => formatDateTime(row.settle_at) },
    ];

    return (
        <AppLayout>
            <div className="space-y-4">
                <div className="rounded-2xl border bg-white p-4 shadow-sm">
                    <div className="mb-4 text-lg font-semibold">{t('invoiceTranRpt')}</div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">{t('dateFrom')}</label>
                            <Input
                                type="date"
                                value={filters.date_from}
                                className={"w-auto h-auto min-h-[40px]"}
                                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">{t('dateTo')}</label>
                            <Input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                                className="w-auto h-auto min-h-[40px]"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">{t("reference")}</label>
                            <Input
                                placeholder={t("ref")}
                                value={filters.invoice_id}
                                onChange={(e) => setFilters({ ...filters, invoice_id: e.target.value })}
                            />
                        </div>
                        {/* <select
                            className="h-10 rounded-md border px-3"
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        >
                            {transactionStatuses.map((x) => (
                                <option key={x.value} value={x.value}>{x.label}</option>
                            ))}
                        </select> */}

                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <Button
                            onClick={onSearch}
                            className="w-full sm:w-auto"
                        >
                            {t('search')}
                        </Button>

                        <Button
                            variant="outline"
                            onClick={onReset}
                            className="w-full sm:w-auto"
                        >
                            {t('clear')}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={onExportExcel}
                            className="w-full sm:w-auto"
                        >
                            {t('exportExcel')}
                        </Button>
                    </div>
                </div>
                <ReportSummaryCards items={summaryItems} />
                <SimpleTable columns={columns} data={rows} />
                <SimplePagination
                    skip={pager.skip}
                    take={pager.take}
                    total={total}
                    // loading={loading}
                    onChange={setPager}
                />
            </div>
        </AppLayout>
    );
};

export default InvoiceTransactionReport;